import React, { useMemo, useState } from "react";
import { Box, Card, CardContent, Typography, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { InsuranceData, MainInsuranceBranch, PremiumType, LifeInsuranceSubCategory } from "../../interfaces/DTO/insurance_data/InsuranceData";
import { formatMainInsuranceBranch, formatPremiumType } from "../../utils/insuranceUtil";
import { formatCurrency } from "../../utils/namesFormater";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { ManualMimshakData } from "../../interfaces/DTO/manualMimshakData";
import { InsuranceToManualMimshakConverter } from "./InsuranceToManualMimshakConverter";
import { Contact } from "../../interfaces/DTO/crm/contact";

interface Props {
  data: InsuranceData[];
  selectedBranch: string | null;
  onBranchClick: (branch: string | null) => void;
  insuranceMimshakData?: MimshakData[];
  manualMimshakData?: ManualMimshakData[];
  contacts: Contact[];
  reportedPeriod: string;
}

interface BranchMetrics {
  branch: string;
  totalPremium: number;
  policyCount: number;
  byPremiumType: Record<string, number>;
  unmatchedCount?: number; // מספר פוליסות ללא matching
  unmatchedPolicies?: InsuranceData[]; // הפוליסות עצמן ללא matching
}

const InsuranceBranchMetrics: React.FC<Props> = ({
  data,
  selectedBranch,
  onBranchClick,
  insuranceMimshakData = [],
  manualMimshakData = [],
  contacts,
  reportedPeriod
}) => {
  const [converterOpen, setConverterOpen] = useState(false);
  const [selectedUnmatchedPolicies, setSelectedUnmatchedPolicies] = useState<InsuranceData[]>([]);

  // פונקציה לבדיקת האם פוליסה יש לה matching ב-mimshak data (אוטומטי או ידני)
  const hasMatchingMimshakData = (insuranceItem: InsuranceData): boolean => {
    // בדיקה ב-insuranceMimshakData (אוטומטי)
    const hasAutoMatch = insuranceMimshakData.some(
      (mimshak) =>
        mimshak.handler_code === insuranceItem.handler_code &&
        mimshak.policy_number === insuranceItem.policy_number &&
        mimshak.contact_id === insuranceItem.contact_id
    );

    if (hasAutoMatch) return true;

    // בדיקה ב-manualMimshakData (ידני)
    const hasManualMatch = manualMimshakData.some(
      (mimshak) =>
        mimshak.handler_code === insuranceItem.handler_code &&
        mimshak.policy_number === insuranceItem.policy_number &&
        mimshak.contact_id === insuranceItem.contact_id
    );

    return hasManualMatch;
  };

  const metrics = useMemo(() => {
    // קבוצות ענפים
    const carBranch = MainInsuranceBranch.CAR;
    const homeBranch = MainInsuranceBranch.HOME;
    const longTermCareBranch = MainInsuranceBranch.LONG_TERM_CARE;

    // חיים - רק חיים ואובדן כושר עבודה (לא כולל חיסכון טהור)
    const lifeBranches = [
      MainInsuranceBranch.LIFE,
      MainInsuranceBranch.LOSS_OF_WORK_CAPACITY,
    ];

    // בריאות - כל השאר (ללא סיעוד)
    const healthBranches = [
      MainInsuranceBranch.DISABILITY_LIFE,
      MainInsuranceBranch.HEALTH_ACCIDENT,
      MainInsuranceBranch.HEALTH,
      MainInsuranceBranch.SERVICE_CONTRACT,
      MainInsuranceBranch.BUSINESS,
    ];

    const branchGroups: Record<string, InsuranceData[]> = {
      [carBranch]: [],
      [homeBranch]: [],
      LIFE: [],
      SAVINGS: [],
      LONG_TERM_CARE: [],
      HEALTH_AND_OTHERS: [],
    };

    // סיווג הנתונים לקבוצות
    data.forEach((item) => {
      if (item.main_branch === carBranch) {
        branchGroups[carBranch].push(item);
      } else if (item.main_branch === homeBranch) {
        branchGroups[homeBranch].push(item);
      } else if (item.main_branch === longTermCareBranch) {
        branchGroups.LONG_TERM_CARE.push(item);
      } else if (item.main_branch === MainInsuranceBranch.LIFE && item.sub_branch === LifeInsuranceSubCategory.PURE_SAVINGS) {
        // חיסכון טהור - קטגוריה נפרדת
        branchGroups.SAVINGS.push(item);
      } else if (lifeBranches.includes(item.main_branch as MainInsuranceBranch)) {
        // חיים - ללא חיסכון טהור
        branchGroups.LIFE.push(item);
      } else {
        branchGroups.HEALTH_AND_OTHERS.push(item);
      }
    });

    // חישוב מטריקות לכל קבוצה
    const calculateMetrics = (branch: string, items: InsuranceData[], checkMatching: boolean = false): BranchMetrics => {
      const byPremiumType: Record<string, number> = {};
      let totalPremium = 0;
      let unmatchedCount = 0;
      const unmatchedPolicies: InsuranceData[] = [];

      items.forEach((item) => {
        const premium = item.premium_amount || 0;
        totalPremium += premium;

        const premiumType = item.premium_type || "UNKNOWN";
        byPremiumType[premiumType] = (byPremiumType[premiumType] || 0) + premium;

        // בדיקת matching רק לחיסכון
        if (checkMatching && !hasMatchingMimshakData(item)) {
          unmatchedCount++;
          unmatchedPolicies.push(item);
        }
      });

      return {
        branch,
        totalPremium,
        policyCount: items.length,
        byPremiumType,
        unmatchedCount: checkMatching ? unmatchedCount : undefined,
        unmatchedPolicies: checkMatching && unmatchedPolicies.length > 0 ? unmatchedPolicies : undefined,
      };
    };

    // בדיקת matching רק אם יש פוליסות חיסכון
    const hasSavings = branchGroups.SAVINGS.length > 0;

    return {
      car: calculateMetrics(carBranch, branchGroups[carBranch]),
      home: calculateMetrics(homeBranch, branchGroups[homeBranch]),
      life: calculateMetrics("LIFE", branchGroups.LIFE),
      savings: calculateMetrics("SAVINGS", branchGroups.SAVINGS, hasSavings), // בדיקת matching רק אם יש חיסכון
      longTermCare: calculateMetrics(longTermCareBranch, branchGroups.LONG_TERM_CARE),
      health: calculateMetrics("HEALTH_AND_OTHERS", branchGroups.HEALTH_AND_OTHERS),
    };
  }, [data, insuranceMimshakData, manualMimshakData]);

  const renderMetricCard = (metrics: BranchMetrics, displayName: string, branchKey: string) => {
    const isSelected = selectedBranch === branchKey;
    const hasData = metrics.policyCount > 0;

    return (
      <Card
        key={branchKey}
        onClick={hasData ? () => onBranchClick(isSelected ? null : branchKey) : undefined}
        sx={{
          minWidth: 250,
          cursor: hasData ? "pointer" : "default",
          border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
          backgroundColor: isSelected ? "#e3f2fd" : "white",
          transition: "all 0.2s",
          opacity: hasData ? 1 : 0.6,
          "&:hover": hasData ? {
            backgroundColor: isSelected ? "#bbdefb" : "#f5f5f5",
            transform: "translateY(-2px)",
            boxShadow: 3,
          } : {},
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            sx={{ color: hasData ? "inherit" : "text.disabled" }}
          >
            {displayName}
          </Typography>

          {/* סכום ופירוט לפי סוג פרמיה */}
          <Box sx={{ mt: 1 }}>
            {hasData ? (
              <>
                {Object.entries(metrics.byPremiumType).map(([type, amount]) => (
                  <Box key={type} sx={{ mb: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="primary"
                    >
                      <span dir="ltr">{formatCurrency(amount)}</span>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatPremiumType(type as PremiumType)}
                    </Typography>
                  </Box>
                ))}
                {/* הצגת מספר פוליסות שלא בתיק (רק לחיסכון) */}
                {metrics.unmatchedCount !== undefined && metrics.unmatchedCount > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="warning.main" display="block">
                      {metrics.unmatchedCount} פוליסות שלא בתיק
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation(); // מניעת הפעלת onClick של הכרטיס
                        setSelectedUnmatchedPolicies(metrics.unmatchedPolicies || []);
                        setConverterOpen(true);
                      }}
                      sx={{ mt: 1 }}
                    >
                      האם להוסיף לנכסים?
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "text.disabled", mb: 1 }}
                >
                  <span dir="ltr">{formatCurrency(0)}</span>
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block">
                  לא נמצאו רשומות
                </Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          סיכום לפי ענף
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {renderMetricCard(metrics.car, "רכב", MainInsuranceBranch.CAR)}
          {renderMetricCard(metrics.home, "דירה", MainInsuranceBranch.HOME)}
          {renderMetricCard(metrics.life, "חיים", "LIFE")}
          {/* חיסכון - מוצג רק אם יש נתונים */}
          {metrics.savings.policyCount > 0 && renderMetricCard(metrics.savings, "חיסכון", "SAVINGS")}
          {renderMetricCard(metrics.longTermCare, "סיעוד", MainInsuranceBranch.LONG_TERM_CARE)}
          {renderMetricCard(metrics.health, "בריאות", "HEALTH_AND_OTHERS")}
        </Box>
      </Box>

      {/* דיאלוג להמרת פוליסות לנכסים ידניים */}
      <InsuranceToManualMimshakConverter
        open={converterOpen}
        onClose={() => setConverterOpen(false)}
        insurancePolicies={selectedUnmatchedPolicies}
        contacts={contacts}
        reportedPeriod={reportedPeriod}
        onPolicySaved={(saved) =>
          setSelectedUnmatchedPolicies((prev) => prev.filter((p) => p.id !== saved.id))
        }
      />
    </>
  );
};

export default InsuranceBranchMetrics;
